import React, { useEffect, useState } from 'react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { msalInstance, loginRequest, normalizeEmailForLookup, fetchAdminRole, roleToPermissions, AdminPermissions } from './authConfig';
import { AdminDashboard } from './components/AdminDashboard';
import { Box, Button, Typography, Paper, CircularProgress, CssBaseline } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Login as LoginIcon } from '@mui/icons-material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { adminApi } from './services/adminApi';

const theme = createTheme({
  palette: {
    primary: { main: '#FF8C00', contrastText: '#ffffff' },
    secondary: { main: '#ffffff' },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
    },
  },
});

function AppContent() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [permissions, setPermissions] = useState<AdminPermissions | null>(null);
  const [displayName, setDisplayName] = useState<string>('');

  useEffect(() => {
    if (isAuthenticated && accounts[0]) {
      const rawUsername = accounts[0].username;
      const email = normalizeEmailForLookup(rawUsername) || rawUsername;

      instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      }).then(async response => {
        adminApi.setAuthToken(response.accessToken);
        const roleData = await fetchAdminRole(email, response.accessToken);
        if (!roleData) {
          console.error('❌ Unauthorized access attempt:', email);
          setIsAuthorized(false);
          return;
        }
        setPermissions(roleToPermissions(roleData.role));
        setDisplayName(roleData.display_name);
        setIsAuthorized(true);
      }).catch(error => {
        console.error('Token acquisition failed:', error);
        setIsAuthorized(false);
      });
    } else {
      setIsAuthorized(null);
      setPermissions(null);
    }
  }, [isAuthenticated, accounts, instance]);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, textAlign: 'center', borderRadius: 4 }}>
          <Typography variant="h4" gutterBottom>
            <span style={{ fontFamily: 'Lobster, cursive', color: '#FF8C00' }}>&nbsp;famēlia</span>
            <span style={{ fontWeight: 400, fontSize: '0.75em', color: '#555' }}> Admin</span>
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Sign in with your Azure credentials to access the admin dashboard
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            onClick={handleLogin}
            fullWidth
          >
            Sign in with Microsoft
          </Button>
        </Paper>
      </Box>
    );
  }

  if (isAuthorized === false) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="#f5f5f5"
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" paragraph>
            You ({normalizeEmailForLookup(accounts[0]?.username) || accounts[0]?.username}) are not authorized to access this dashboard.
          </Typography>
          <Button variant="outlined" onClick={handleLogout}>
            Sign Out
          </Button>
        </Paper>
      </Box>
    );
  }

  if (!permissions) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AdminDashboard
      onLogout={handleLogout}
      userEmail={normalizeEmailForLookup(accounts[0]?.username) || accounts[0]?.username}
      permissions={permissions}
      displayName={displayName}
    />
  );
}

function App() {
  const [msalInitialized, setMsalInitialized] = useState(false);

  useEffect(() => {
    msalInstance.initialize().then(() => {
      msalInstance.handleRedirectPromise().then(() => {
        setMsalInitialized(true);
      }).catch((error) => {
        console.error('Redirect handling error:', error);
        setMsalInitialized(true); // Still show the app even if redirect fails
      });
    });
  }, []);

  if (!msalInitialized) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <CssBaseline />
        <MsalProvider instance={msalInstance}>
          <AppContent />
        </MsalProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
